import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FloorplanArrow } from '@/types';

interface EntranceArrowProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  frame: { width: number; height: number };
  arrow: FloorplanArrow;
  onChange: (next: FloorplanArrow) => void;
  /** When false, only the bare triangle is rendered (no handles, no outline). */
  isSelected: boolean;
  /** Called when the user clicks the triangle body to select it. */
  onSelect: () => void;
}

const SIZE_MIN = 0.03;
const SIZE_MAX = 0.25;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const SNAP_TOL = 5;
const SNAP_ANGLES = [0, 90, 180, 270, 360];
function snapRightAngle(deg: number): number {
  const norm = ((deg % 360) + 360) % 360;
  for (const s of SNAP_ANGLES) {
    if (Math.abs(norm - s) <= SNAP_TOL) return s === 360 ? 0 : s;
  }
  return norm;
}

/**
 * Draggable + rotatable + resizable entrance-triangle overlay (classic German
 * Eingangsdreieck) positioned absolutely over the floorplan canvas. Follows
 * the same Canva-style direct-manipulation pattern as BrandingCanvasHandles:
 * body-drag to reposition, 4 corner handles for uniform scale, rotate handle
 * below for rotation around the triangle's center.
 */
export const EntranceArrow: React.FC<EntranceArrowProps> = ({
  canvasRef,
  frame,
  arrow,
  onChange,
  isSelected,
  onSelect,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [visibleRect, setVisibleRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const update = () => setVisibleRect(canvas.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(canvas);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [canvasRef]);

  // Drag body → update x, y. Always selects on pointer-down so a single click
  // both selects the triangle and arms a drag.
  const onDragPointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    if (!visibleRect) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startArrowX = arrow.x;
    const startArrowY = arrow.y;

    const onMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / visibleRect.width;
      const dy = (ev.clientY - startY) / visibleRect.height;
      onChange({
        ...arrow,
        x: Math.max(0, Math.min(1, startArrowX + dx)),
        y: Math.max(0, Math.min(1, startArrowY + dy)),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [arrow, visibleRect, onChange, onSelect]);

  // Rotate handle → update rotation based on angle from arrow center
  const onRotatePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!visibleRect) return;
    const centerX = visibleRect.left + visibleRect.width * arrow.x;
    const centerY = visibleRect.top + visibleRect.height * arrow.y;

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - centerX;
      const dy = ev.clientY - centerY;
      // atan2 returns 0 at +X axis. We want 0 when the handle (below center, at rotation=0
      // default position) points south-of-center. So angle = atan2(dy, dx) - 90°.
      const rawDeg = ((Math.atan2(dy, dx) * 180) / Math.PI - 90 + 360) % 360;
      onChange({ ...arrow, rotation: snapRightAngle(rawDeg) });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [arrow, visibleRect, onChange]);

  // Corner-handle uniform scale: distance-from-center approach — rotation-invariant.
  // scaleFactor = hypot(pointer - arrowCenter) / hypot(startPointer - arrowCenter)
  const onCornerPointerDown = useCallback(
    (_handleId: 'nw' | 'ne' | 'sw' | 'se') => (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!visibleRect) return;

      // Arrow center in screen coords
      const centerX = visibleRect.left + visibleRect.width * arrow.x;
      const centerY = visibleRect.top + visibleRect.height * arrow.y;
      const startDist = Math.hypot(e.clientX - centerX, e.clientY - centerY);
      const startSize = arrow.size;

      const onMove = (ev: PointerEvent) => {
        const currentDist = Math.hypot(ev.clientX - centerX, ev.clientY - centerY);
        const factor = currentDist / Math.max(1, startDist);
        onChange({ ...arrow, size: clamp(startSize * factor, SIZE_MIN, SIZE_MAX) });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [arrow, visibleRect, onChange]
  );

  if (!visibleRect) return null;

  const scaleX = visibleRect.width / frame.width;
  const sizePx = arrow.size * frame.width * scaleX; // diameter in DOM px
  const centerX = visibleRect.left + visibleRect.width * arrow.x;
  const centerY = visibleRect.top + visibleRect.height * arrow.y;

  const HANDLE_SIZE = 12;
  const ROTATE_SIZE = 20;
  const ROTATE_STEM = 14;

  return (
    <div
      ref={overlayRef}
      className="fixed pointer-events-none"
      style={{
        left: centerX - sizePx / 2,
        top: centerY - sizePx / 2,
        width: sizePx,
        height: sizePx,
        transform: `rotate(${arrow.rotation}deg)`,
        transformOrigin: 'center',
      }}
    >
      {/* Drag body = the triangle glyph itself */}
      <div
        className="absolute inset-0 pointer-events-auto cursor-move"
        onPointerDown={onDragPointerDown}
      >
        <svg viewBox="0 0 24 24" className="w-full h-full" aria-label="Eingangsdreieck">
          <polygon points="12,2 22,22 2,22" fill="#111" stroke="#fff" strokeWidth="1" />
        </svg>
      </div>

      {/* Selection outline + handles — only visible while selected */}
      {isSelected && (
        <>
          <div className="absolute inset-0 border border-primary/60 pointer-events-none" />

          {/* 4 corner handles for uniform resize */}
          {(['nw', 'ne', 'sw', 'se'] as const).map((id) => {
            const isTop = id.startsWith('n');
            const isLeft = id.endsWith('w');
            const cursor = id === 'nw' || id === 'se' ? 'nwse-resize' : 'nesw-resize';
            return (
              <div
                key={id}
                className="absolute bg-white border-2 border-primary rounded-full shadow-sm pointer-events-auto"
                style={{
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  [isTop ? 'top' : 'bottom']: -HANDLE_SIZE / 2,
                  [isLeft ? 'left' : 'right']: -HANDLE_SIZE / 2,
                  cursor,
                }}
                onPointerDown={onCornerPointerDown(id)}
              />
            );
          })}

          {/* Rotate handle — stem + circle, pinned below the triangle's rotating frame */}
          <div
            className="absolute left-1/2 flex flex-col items-center pointer-events-none"
            style={{ bottom: -(ROTATE_STEM + ROTATE_SIZE + 4), transform: 'translateX(-50%)' }}
          >
            <div className="w-px bg-primary/50" style={{ height: ROTATE_STEM }} />
            <div
              className="bg-white border-2 border-primary rounded-full shadow-md flex items-center justify-center text-primary pointer-events-auto"
              style={{ width: ROTATE_SIZE, height: ROTATE_SIZE, cursor: 'grab' }}
              onPointerDown={onRotatePointerDown}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <polyline points="3 4 3 9 8 9" />
              </svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
